import { ElementBlockConflictsWithRequiredFormPrivacyRule } from '@fullstory/client-api';
import { NamedElementBlockRule } from '@fullstory/proto/fs/services/ingestion-settings';
import { RuleScope } from '@fullstory/proto/fs/services/orgs';
import { useNotifications } from '@fullstory/react-hooks';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { fullstorySelectors } from '../../selectors';
import { AppDispatch } from '../../types';
import { NamedElementBlockRulesActions } from './actions';
import { SettingsRequestActions } from '../settings';

export const useNamedElementBlockRuleFormPrivacyActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const intl = useIntl();
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const updateFormPrivacy = useCallback(
    async (groupId: number, ruleScope: RuleScope) => {
      try {
        const rsp = await dispatch(
          NamedElementBlockRulesActions.updateFormPrivacyConfiguration({
            orgId,
            groupId,
            ruleScope,
          }),
        );
        await dispatch(SettingsRequestActions.getPrivacySettings());
        notifications?.success(
          intl.formatMessage({
            defaultMessage: 'Successfully updated form privacy configuration.',
            id: 'ezgf/D',
          }),
        );
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to update form privacy configuration.',
            id: '/4/T07',
          }),
        );
        return null;
      }
    },
    [dispatch, intl, notifications, orgId],
  );
  return {
    updateFormPrivacy,
  };
};

export const useNamedElementBlockRuleActions = () => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const intl = useIntl();
  const [blockRules, setBlockRules] = useState<NamedElementBlockRule[]>([]);

  useEffect(() => {
    if (orgId) {
      dispatch(NamedElementBlockRulesActions.getAllNamedElementBlockRules({ orgId }))
        .then(rsp => {
          setBlockRules(rsp.namedElementBlockRules ?? []);
        })
        .catch(() => {
          notifications?.error(
            intl.formatMessage({
              defaultMessage: 'Failed to fetch data capture rules.',
              id: '39Qe1V',
            }),
          );
        });
    }
  }, [dispatch, intl, notifications, orgId]);

  const updateRule = useCallback(
    async (namedElementBlockRule: NamedElementBlockRule, previousSelector: string) => {
      try {
        const rsp = await dispatch(
          NamedElementBlockRulesActions.updateNamedElementBlockRule({
            orgId,
            namedElementBlockRule,
            previousSelector,
          }),
        );

        notifications?.success(
          intl.formatMessage({
            defaultMessage: 'Successfully updated the data capture rules.',
            id: 'ns9h6e',
          }),
        );
        return rsp;
      } catch (e: any) {
        if (e.response?.Message === ElementBlockConflictsWithRequiredFormPrivacyRule) {
          notifications.error(
            intl.formatMessage({
              defaultMessage:
                'This exception will un-do the Form Privacy rule. Please create a more specific exception.',
              id: 'bSyQDR',
            }),
          );
          return null;
        } else {
          notifications?.error(
            intl.formatMessage({
              defaultMessage: 'Failed to update the data capture rules.',
              id: 'ibyWYf',
            }),
          );
        }
        return null;
      }
    },
    [dispatch, intl, notifications, orgId],
  );

  const deleteRule = useCallback(
    async (namedElementBlockRuleId: string) => {
      try {
        const rsp = await dispatch(
          NamedElementBlockRulesActions.deleteNamedElementBlockRule({
            orgId,
            namedElementBlockRuleId,
          }),
        );

        notifications?.success(
          intl.formatMessage({
            defaultMessage: 'Successfully deleted data capture rule.',
            id: '7gFCJG',
          }),
        );
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to delete data capture rule.',
            id: 'amiFtr',
          }),
        );
        return null;
      }
    },
    [dispatch, intl, notifications, orgId],
  );

  return {
    blockRules,
    updateRule,
    deleteRule,
  };
};
